import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Users, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Trash2, 
  CheckCircle, 
  Circle,
  RefreshCw,
  ArrowLeft,
  Lock,
  LogOut,
  Eye,
  EyeOff
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, by_source: {} });
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  // Check if already authenticated
  useEffect(() => {
    const authStatus = sessionStorage.getItem('adminAuth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        sessionStorage.setItem('adminAuth', 'true');
        setIsAuthenticated(true);
      } else {
        setLoginError('Password non corretta');
      }
    } catch (error) {
      setLoginError('Errore di connessione');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
    setPassword('');
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const [contactsRes, statsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts/stats`)
      ]);
      
      if (contactsRes.ok && statsRes.ok) {
        const contactsData = await contactsRes.json();
        const statsData = await statsRes.json();
        setContacts(contactsData);
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchContacts();
    }
  }, [isAuthenticated]);

  const markAsRead = async (contactId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contacts/${contactId}/read`,
        { method: 'PATCH' }
      );
      if (response.ok) {
        fetchContacts();
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo contatto?')) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contacts/${contactId}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        setSelectedContact(null);
        fetchContacts();
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSourceLabel = (source) => {
    const labels = {
      'contatti': 'Pagina Contatti',
      'professionisti': 'Per Professionisti',
    };
    return labels[source] || source;
  };

  const getSourceColor = (source) => {
    const colors = {
      'contatti': 'bg-blue-100 text-blue-800',
      'professionisti': 'bg-orange-100 text-orange-800',
    };
    return colors[source] || 'bg-gray-100 text-gray-800';
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="pt-20 min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4 shadow-2xl">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-10 w-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold">Area Riservata</h1>
              <p className="text-gray-500 mt-2">Inserisci la password per accedere</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Inserisci password"
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {loginError && (
                <p className="text-red-500 text-sm text-center">{loginError}</p>
              )}

              <Button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-[var(--verdigris)] hover:bg-[var(--verdigris-dark)]"
              >
                {loginLoading ? 'Verifica...' : 'Accedi'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-gray-500 hover:text-[var(--copper-primary)]">
                ← Torna al sito
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="pt-20 min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] text-white py-12">
        <div className="container">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors">
                <ArrowLeft className="h-4 w-4" />
                Torna al sito
              </Link>
              <h1 className="text-4xl font-bold">Gestione Contatti</h1>
              <p className="text-white/80 mt-2">Visualizza e gestisci tutte le richieste di contatto</p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={fetchContacts}
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[var(--verdigris)]"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Aggiorna
              </Button>
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="border-white/50 text-white hover:bg-red-500 hover:border-red-500"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Totale Contatti</p>
                  <p className="text-3xl font-bold">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Da Leggere</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.unread}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Mail className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Da Contatti</p>
                  <p className="text-3xl font-bold">{stats.by_source?.contatti || 0}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Phone className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Da Professionisti</p>
                  <p className="text-3xl font-bold">{stats.by_source?.professionisti || 0}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contacts List */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-0">
                <div className="p-4 border-b">
                  <h2 className="text-xl font-semibold">Lista Contatti</h2>
                </div>
                
                {loading ? (
                  <div className="p-8 text-center text-gray-500">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
                    Caricamento...
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nessun contatto ricevuto</p>
                    <p className="text-sm">I nuovi contatti appariranno qui</p>
                  </div>
                ) : (
                  <div className="divide-y max-h-[600px] overflow-y-auto">
                    {contacts.map((contact) => (
                      <div
                        key={contact.id}
                        onClick={() => {
                          setSelectedContact(contact);
                          if (!contact.read) markAsRead(contact.id);
                        }}
                        className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedContact?.id === contact.id ? 'bg-blue-50' : ''
                        } ${!contact.read ? 'bg-orange-50' : ''}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            {contact.read ? (
                              <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                            ) : (
                              <Circle className="h-5 w-5 text-orange-500 mt-1 flex-shrink-0" />
                            )}
                            <div>
                              <p className={`font-semibold ${!contact.read ? 'text-orange-800' : ''}`}>
                                {contact.name}
                              </p>
                              <p className="text-sm text-gray-500">{contact.email || contact.phone}</p>
                              <p className="text-sm text-gray-400 mt-1 line-clamp-1">
                                {contact.message}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <span className={`text-xs px-2 py-1 rounded-full ${getSourceColor(contact.source)}`}>
                              {getSourceLabel(contact.source)}
                            </span>
                            <p className="text-xs text-gray-400 mt-2">
                              {formatDate(contact.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Contact Detail */}
          <div>
            <Card className="sticky top-24">
              <CardContent className="p-0">
                <div className="p-4 border-b">
                  <h2 className="text-xl font-semibold">Dettaglio Contatto</h2>
                </div>
                
                {selectedContact ? (
                  <div className="p-4 space-y-4">
                    <div>
                      <p className="text-sm text-gray-500">Nome</p>
                      <p className="font-semibold text-lg">{selectedContact.name}</p>
                    </div>
                    
                    {selectedContact.email && (
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <a 
                          href={`mailto:${selectedContact.email}`}
                          className="text-[var(--copper-primary)] hover:underline"
                        >
                          {selectedContact.email}
                        </a>
                      </div>
                    )}
                    
                    {selectedContact.phone && (
                      <div>
                        <p className="text-sm text-gray-500">Telefono</p>
                        <a 
                          href={`tel:${selectedContact.phone}`}
                          className="text-[var(--copper-primary)] hover:underline"
                        >
                          {selectedContact.phone}
                        </a>
                      </div>
                    )}
                    
                    {selectedContact.comune && (
                      <div>
                        <p className="text-sm text-gray-500">Comune</p>
                        <p>{selectedContact.comune}</p>
                      </div>
                    )}
                    
                    {selectedContact.kg_stimati && (
                      <div>
                        <p className="text-sm text-gray-500">Kg Stimati</p>
                        <p>{selectedContact.kg_stimati}</p>
                      </div>
                    )}
                    
                    {selectedContact.subject && (
                      <div>
                        <p className="text-sm text-gray-500">Oggetto</p>
                        <p>{selectedContact.subject}</p>
                      </div>
                    )}
                    
                    <div>
                      <p className="text-sm text-gray-500">Messaggio</p>
                      <p className="whitespace-pre-wrap bg-gray-50 p-3 rounded-lg mt-1">
                        {selectedContact.message}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500">Provenienza</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${getSourceColor(selectedContact.source)}`}>
                        {getSourceLabel(selectedContact.source)}
                      </span>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500">Data</p>
                      <p className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {formatDate(selectedContact.created_at)}
                      </p>
                    </div>
                    
                    <div className="pt-4 border-t flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteContact(selectedContact.id)}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Elimina
                      </Button>
                      {selectedContact.email && (
                        <a href={`mailto:${selectedContact.email}`}>
                          <Button size="sm" className="bg-[var(--copper-primary)] hover:bg-[var(--copper-dark)]">
                            <Mail className="h-4 w-4 mr-2" />
                            Rispondi
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Seleziona un contatto</p>
                    <p className="text-sm">per vedere i dettagli</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
